import {
  Component,
  QueryList,
  ContentChildren,
  Input,
  Output,
  EventEmitter,
  HostBinding,
  OnChanges,
  SimpleChanges,
  AfterContentInit,
  OnDestroy,
} from "@angular/core";
import { trigger, transition, animate, style } from "@angular/animations";
import { Subscription } from "rxjs";
import { DsSegmentedOptionComponent } from "../ds-segmented-option/ds-segmented-option.component";
import { SegmentedControlSize } from "../types/segmented-control.types";

@Component({
  selector: "ds-segmented-control",
  templateUrl: "./ds-segmented-control.component.html",
  styleUrls: ["./ds-segmented-control.component.scss"],
  animations: [
    trigger("contentAnimation", [
      transition("* => *", [
        style({ opacity: 0, transform: "translateY(10px)" }),
        animate("200ms ease-out", style({ opacity: 1, transform: "translateY(0)" })),
      ]),
    ]),
  ],
})
export class DsSegmentedControlComponent implements OnChanges, AfterContentInit, OnDestroy {
  @Input() selectedIndex: number | null = 0;
  @Input() label = "";
  @Input() fullWidth = false;
  /** Sizes every option from the widest projected label instead of the host width. */
  @Input() fitContent = false;
  /** Controls the density of every projected option ("lg" is the default). */
  @Input() size: SegmentedControlSize = "lg";

  @HostBinding("class.ds-segmented-control--fit-content")
  get fitContentHostClass(): boolean {
    return this.fitContent;
  }

  /**
   * When true, a null selectedIndex renders no active option and
   * does not auto-select the first enabled one.
   */
  @Input() allowUnselected = false;

  @ContentChildren(DsSegmentedOptionComponent) options!: QueryList<DsSegmentedOptionComponent>;

  @Output() selectedOptionChange = new EventEmitter<number | null>();

  private subscriptions: Subscription[] = [];
  private optionClickSubscriptions: Subscription[] = [];

  constructor() {}

  ngAfterContentInit() {
    // Subscribe to current option clicks
    this.subscribeToOptionClicks();

    // Push the current size down to every projected option
    this.updateOptionSizes();

    // Set initial active option
    this.setActiveOption(this.selectedIndex);

    // Watch for dynamic changes to options (e.g., *ngFor add/remove or disabled
    // state changes). Re-subscribe to clicks so dynamically projected options
    // remain interactive; the initial QueryList can be empty when content is
    // rendered with a structural directive.
    const optionsChangesSubscription = this.options.changes.subscribe(() => {
      this.subscribeToOptionClicks();
      this.updateOptionSizes();
      this.setActiveOption(this.selectedIndex);
    });
    this.subscriptions.push(optionsChangesSubscription);
  }

  /** Propagate the control size to every currently projected option. */
  private updateOptionSizes(): void {
    this.options?.forEach((option) => (option.size = this.size));
  }

  ngOnDestroy() {
    // Clean up all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.optionClickSubscriptions.forEach((sub) => sub.unsubscribe());
  }

  /** (Re)subscribe to the click event of every currently projected option. */
  private subscribeToOptionClicks(): void {
    this.optionClickSubscriptions.forEach((sub) => sub.unsubscribe());
    this.optionClickSubscriptions = [];

    this.options.forEach((option, index) => {
      const subscription = option.select.subscribe(() => {
        this.selectedIndex = index;
        this.selectedOptionChange.emit(index);
        this.setActiveOption(index);
      });
      this.optionClickSubscriptions.push(subscription);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle selectedIndex changes from parent
    // Check if options exist to avoid timing issues (ngOnChanges is called before ngAfterContentInit)
    if (changes["selectedIndex"] && !changes["selectedIndex"].firstChange && this.options && this.options.length > 0) {
      this.setActiveOption(this.selectedIndex);
    }

    // Re-apply the size to every option when the parent toggles it at runtime
    if (changes["size"] && !changes["size"].firstChange && this.options && this.options.length > 0) {
      this.updateOptionSizes();
    }
  }

  /**
   * Set the active option by index
   * If the target option is disabled, find the first enabled option
   */
  private setActiveOption(index: number | null): void {
    const targetOption = index !== null ? this.options.get(index) : null;

    // Explicit "nothing selected" state: leave every option inactive.
    if (index === null && this.allowUnselected) {
      this.selectedIndex = null;
      this.options.forEach((o) => (o.active = false));
      return;
    }

    if (targetOption && !targetOption.disabled) {
      // Set the target option as active
      this.options.forEach((o, i) => {
        o.active = i === index;
      });
    } else {
      // Target is disabled or doesn't exist, find first enabled option
      const firstEnabledOption = this.findFirstEnabledOption();
      if (firstEnabledOption) {
        const firstEnabledIndex = this.options.toArray().indexOf(firstEnabledOption);
        this.selectedIndex = firstEnabledIndex;
        this.options.forEach((o, i) => {
          o.active = i === firstEnabledIndex;
        });
        // Emit change event to sync parent component state
        this.selectedOptionChange.emit(firstEnabledIndex);
      } else {
        // No enabled options available
        this.selectedIndex = null;
        // Emit change event to sync parent component state
        this.selectedOptionChange.emit(null);
      }
    }
  }

  get activeOption(): DsSegmentedOptionComponent | undefined {
    return this.selectedIndex !== null ? this.options.get(this.selectedIndex) : undefined;
  }

  private findFirstEnabledOption(): DsSegmentedOptionComponent | undefined {
    return this.options.find((option) => !option.disabled);
  }
}
